import { Fragment, useEffect, useRef, useState } from "react";

import { useApolloClient, useMutation } from "@apollo/client";

import { RadioButton } from "primereact/radiobutton";
import { FileUpload, FileUploadHandlerParam } from "primereact/fileupload";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_FRANCHISE, { UPDATE_FRANCHISE } from "../../provider/Franchises/mutation";
import { FRANCHISES } from "../../provider/Franchises/query";
import UPLOAD_FILE from "../../provider/Upload/mutation";

/** utils */
import { getGraphsqErros } from "../../utilities/functions";

const Crud = () => {
  const [uploadFile] = useMutation(UPLOAD_FILE);
  const settings = useSettings();
  const apollo = useApolloClient();

  const toast = useRef<any>(null);
  const dt = useRef<any>(null);

  const [records, setRecords] = useState<any[]>([]);
  const [record, setRecord] = useState<iFranchise>();

  const [loading, setLoading] = useState<boolean>(false);
  const [recordDialog, setRecordDialog] = useState<boolean>(false);
  const [deleteRecordDialog, setDeleteRecordDialog] = useState<boolean>(false);

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  let emptyRecord: iFranchise = {
    name: "",
    driver_quantity: 1,
    is_actived: true,
    logo_129: '',
    logo_258: '',
    logo_387: '',
    logo_512: '',
    logo_path: '',
    banner_path: '',
    mercado_pago_access_token: '',
    google_api_key  : '',
    firebase_api_key  : '',
    firebase_auth_domain  : '',
    firebase_project_id  : '',
    firebase_storage_bucket  : '',
    firebase_sender_id  : '',
    firebase_app_id  : '',
    firebase_measurement_id  : '',
    firebase_database_url  : '',
    firebase_private_key_id  : '',
    firebase_private_key  : '',
    firebase_client_email  : '',
    firebase_client_id  : '',
    client_x509_cert_url  : '',
    website_link: '',
    color_danger: 'FF0000',
    color_primary: 'FF5C00',
    color_primary_ligth: 'EBA19D',
    color_secondary: '0D1724',
    color_yellow: 'e8e616',
    color_grey: '737373',
    color_grey_ligth: 'F8F8F8',
    color_grey_ligth2: 'eee',
    color_white: 'FFFFFF',
    color_black: '0D1724',
    color_black95: '00000095',
    color_success: '8CC63F',
    color_green_light: '5FBE83',
    color_info: '3C82FF',
    color_warning: 'FFC000',
    color_principal: '00ff43',
    color_base: '000000',
  };

  const upload = (e: FileUploadHandlerParam, key: string) => {
    const file = e.files;

    if (!file || file.length <= 0) return;

    uploadFile({
      variables: {
        file: file[0],
      },
    })
      .then(res => {
        let _record: any = { ...record };
        _record[key] = res.data.singleUpload;
        setRecord(_record);
      })
      .catch(error => {
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  };

  const loadRecords = (cleanRecord: boolean = false) => {
    setLoading(true);

    apollo
      .query({
        query: FRANCHISES,
        variables: {
          // limit: perPage,
          // filter: filter,
        },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.franchises;

        setRecords(data);
        setLoading(false);
      })
      .catch(error => {
        // console.log('error: ', JSON.stringify(error));
        setRecords([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
      });
  };

  const openNew = () => {
    setRecord(emptyRecord);
    setSubmitted(false);
    setRecordDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setRecordDialog(false);
  };

  const hideDeleteRecordDialog = () => {
    setDeleteRecordDialog(false);
  };

  const saveRecord = () => {
    setSubmitted(true);

    if (!record?.name) {
      return;
    }

    const _record: any = { ...record };

    delete _record.__typename;

    setLoading(true);
    let mutation = CREATE_FRANCHISE;

    if (record?.id) {
      _record.id = parseInt(`${_record.id}`);
      mutation = UPDATE_FRANCHISE;
    }

    apollo
      .mutate({
        mutation,
        variables: {
          data: {
            ..._record,
          },
        },
      })
      .then(response => {
        setLoading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

        loadRecords();
        setRecordDialog(false);
        setRecord(emptyRecord);
      })
      .catch(error => {
        // console.log('error: ', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const editRecord = (record: iFranchise) => {
    const _record: iFranchise = { ...record };

    setRecord({ ..._record });
    setRecordDialog(true);
  };

  const confirmDeleteRecord = (record: any) => {
    setRecord(record);
    setDeleteRecordDialog(true);
  };

  const deleteRecord = () => {
    apollo
      .mutate({
        mutation: UPDATE_FRANCHISE,
        variables: {
          data: {
            id: parseInt(`${record?.id}`),
            is_deleted: true,
          },
        },
      })
      .then(response => {
        let _records = records.filter((val: any) => val.id !== record?.id);
        setRecords(_records);
        setDeleteRecordDialog(false);
        setRecord(emptyRecord);
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
      })
      .catch(error => {
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const onRoleChange = (e: any) => {
    let _record: any = { ...record };
    _record.is_actived = e.value;
    setRecord(_record);
  };

  const onInputChange = (e: any, name: string) => {
    let val = (e.target && e.target.value) || "";

    if(e.target.type === 'number') {
      val = Number(val);
    }

    let _record: any = { ...record };
    _record[name] = val;

    setRecord(_record);
  };

  const leftToolbarTemplate = () => {
    return (
      <Fragment>
        <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
        {/* <Button
          label="Delete"
          icon="pi pi-trash"
          className="p-button-danger mb-2"
          onClick={confirmDeleteSelected}
          disabled={!selectedRecords || !selectedRecords.length}
        /> */}
      </Fragment>
    );
  };

  const codeBodyTemplate = (rowData: iFranchise) => {
    return (
      <>
        <span className="p-column-title">ID</span>
        {rowData.id}
      </>
    );
  };

  const nameBodyTemplate = (rowData: iFranchise) => {
    return (
      <>
        <span className="p-column-title">Nome</span>
        {rowData.name}
      </>
    );
  };

  const driverQuantityBodyTemplate = (rowData: iFranchise) => {
    return (
      <>
        <span className="p-column-title">Quantidade de Motoristas</span>
        {rowData.driver_quantity}
      </>
    );
  };

  const actionBodyTemplate = (rowData: iFranchise) => {
    return (
      <div className="actions">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">{loading ? "Carregando franquias..." : "Franquias"}</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
      </span>
    </div>
  );

  const recordDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
    </>
  );

  const deleteRecordDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
    </>
  );

  useEffect(() => {
    loadRecords(true);
  }, []);

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <Toast ref={toast} />
          <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

          <DataTable
            ref={dt}
            value={records}
            dataKey="id"
            paginator
            stripedRows={true}
            rowHover
            showGridlines
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            className="datatable-responsive"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            globalFilter={globalFilter}
            emptyMessage="Nenhum registro encontrado"
            header={header}
            responsiveLayout="scroll">
            <Column field="id" header="ID" sortable body={codeBodyTemplate} headerStyle={{}} />
            <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}} />
            <Column field="driver_quantity" header="Quantidade de Motoristas" sortable body={driverQuantityBodyTemplate} headerStyle={{}} />

            <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate}></Column>
          </DataTable>

          {/* Modal adicionar editar */}
          <Dialog
            visible={recordDialog}
            style={{ width: "550px" }}
            header={record?.id ? "Detalhes da Franquia" : "Nova Franquia"}
            modal
            className="p-fluid"
            footer={recordDialogFooter}
            onHide={hideDialog}>
            <div className="flex flex-column md:flex-row flex-wrap">
              <div className="field col-12 md:col-12">
                <label htmlFor="name">* Nome</label>
                <InputText
                  id="name"
                  value={record?.name}
                  onChange={e => onInputChange(e, "name")}
                  required
                  autoFocus
                  className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                />
                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
              </div>
            </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="driver_quantity">* Quantidade de motoristas</label>
                  <InputText
                    id="driver_quantity"
                    type={"number"}
                    value={record?.driver_quantity}
                    onChange={e => {
                      onInputChange(e, "driver_quantity");
                    }}
                    required
                    step={1}
                    min={1}
                    className={classNames("p-3", { "p-invalid": submitted && !record?.is_actived })}
                  />
                  {submitted && !record?.driver_quantity && <small className="p-invalid">Informe a Quantidade de motoristas</small>}
                </div>

                {record?.logo_path && record.banner_path ? (
                  <div className="flex flex-column md:flex-row">
                    <img
                      src={record?.logo_path}
                      alt={record?.name}
                      width="100"
                      height={100}
                      style={{ objectFit: "contain" }}
                      className="mt-0 mx-auto mb-5 block shadow-2"
                    />
                    <img
                      src={record?.banner_path}
                      alt={record?.name}
                      width="100"
                      height={100}
                      style={{ objectFit: "contain" }}
                      className="mt-0 mx-auto mb-5 block shadow-2"
                    />
                  </div>
                ) : null}

                <div className="flex flex-column md:flex-row flex-wrap ">
                  <div className="field col-12 md:col-12">
                    <label className="mb-3">Logo</label>

                    <FileUpload
                      chooseLabel="Procurar"
                      auto
                      name="file"
                      customUpload={true}
                      uploadHandler={e => upload(e, "logo_path")}
                      accept="image/*"
                      maxFileSize={1000000}
                    />
                  </div>

                  <div className="field col-12 md:col-12">
                    <label className="mb-3">Banner</label>

                    <FileUpload
                      chooseLabel="Procurar"
                      auto
                      name="file"
                      customUpload={true}
                      uploadHandler={e => upload(e, "banner_path")}
                      accept="image/*"
                      maxFileSize={1000000}
                    />
                  </div>
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="latitude">Latitude</label>
                  <InputText
                    id="latitude"
                    value={record?.latitude}
                    type="number"
                    onChange={e => {
                      onInputChange(e, "latitude");
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="longitude">Longitude</label>
                  <InputText
                    id="longitude"
                    type="number"
                    value={record?.longitude}
                    onChange={e => {
                      onInputChange(e, "longitude");
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="google_api_key">Gooogle API KEY</label>
                  <InputText
                    id="google_api_key"
                    value={record?.google_api_key}
                    onChange={e => {
                      onInputChange(e, "google_api_key");
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="whatsapp_phone">WhatsApp Phone (DDI + DDD + NUM) (Ex: 5522999998888)</label>
                  <InputText
                    id="whatsapp_phone"
                    value={record?.whatsapp_phone}
                    onChange={e => {
                      onInputChange(e, "whatsapp_phone");
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="whatsapp_phone_id">WhatsApp Phone ID</label>
                  <InputText
                    id="whatsapp_phone_id"
                    value={record?.whatsapp_phone_id}
                    onChange={e => {
                      onInputChange(e, "whatsapp_phone_id");
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="whatsapp_token">WhatsApp Token</label>
                  <InputText
                    id="whatsapp_token"
                    value={record?.whatsapp_token}
                    onChange={e => {
                      onInputChange(e, "whatsapp_token");
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="mercado_pago_access_token">MercadoPago Access Token</label>
                  <InputText
                    id="mercado_pago_access_token"
                    value={record?.mercado_pago_access_token}
                    onChange={e => {
                      onInputChange(e, "mercado_pago_access_token");
                    }}
                  />
                </div>
              <div className="flex flex-column md:flex-row flex-wrap ">
                <h6>Firebase</h6>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_api_key">API KEY</label>
                  <InputText
                    id="firebase_api_key"
                    value={record?.firebase_api_key || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_api_key");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_auth_domain">Auth domain</label>
                  <InputText
                    id="firebase_auth_domain"
                    value={record?.firebase_auth_domain || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_auth_domain");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_project_id">Project ID</label>
                  <InputText
                    id="firebase_project_id"
                    value={record?.firebase_project_id || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_project_id");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_storage_bucket">Storage Bucket</label>
                  <InputText
                    id="firebase_storage_bucket"
                    value={record?.firebase_storage_bucket || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_storage_bucket");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_sender_id">Sender ID</label>
                  <InputText
                    id="firebase_sender_id"
                    value={record?.firebase_sender_id || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_sender_id");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_app_id">App ID</label>
                  <InputText
                    id="firebase_app_id"
                    value={record?.firebase_app_id || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_app_id");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_measurement_id">Measurement ID</label>
                  <InputText
                    id="firebase_measurement_id"
                    value={record?.firebase_measurement_id || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_measurement_id");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_database_url">Database URL</label>
                  <InputText
                    id="firebase_database_url"
                    value={record?.firebase_database_url || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_database_url");
                    }}
                  />
                </div>
                <h6>serviceAccountKey.json</h6>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_private_key_id">Private Key ID</label>
                  <InputText
                    id="firebase_private_key_id"
                    value={record?.firebase_private_key_id || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_private_key_id");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_private_key">Private Key</label>
                  <InputText
                    id="firebase_private_key"
                    value={record?.firebase_private_key || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_private_key");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_client_email">Client email</label>
                  <InputText
                    id="firebase_client_email"
                    value={record?.firebase_client_email || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_client_email");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="firebase_client_id">Client ID</label>
                  <InputText
                    id="firebase_client_id"
                    value={record?.firebase_client_id || ''}
                    onChange={e => {
                      onInputChange(e, "firebase_client_id");
                    }}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="client_x509_cert_url">x509 cert URL</label>
                  <InputText
                    id="client_x509_cert_url"
                    value={record?.client_x509_cert_url || ''}
                    onChange={e => {
                      onInputChange(e, "client_x509_cert_url");
                    }}
                  />
                </div>
              </div>
            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">* Situação</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role1" name="is_actived" value={true} onChange={onRoleChange} checked={record?.is_actived} />
                    <label htmlFor="role1">Ativo</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role2" name="is_actived" value={false} onChange={onRoleChange} checked={!record?.is_actived} />
                    <label htmlFor="role2">Inativo</label>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>

          {/* Modal remover registro unico */}
          <Dialog
            visible={deleteRecordDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordDialogFooter}
            onHide={hideDeleteRecordDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && (
                <span>
                  Tem certeza de que deseja excluir <b>{record?.name}</b>?
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Crud;
